import { Fragment, useMemo, useState } from 'react';
import { Empty, ErrorBanner, Loading } from '../../components/ui';
import { formatDate, maturityLabel, maturityLevel, maturityPercent, maturityToneClass } from '../../lib/format';
import { useResource } from '../../lib/hooks';
import {
  answerChanges,
  companyGroups,
  companyTrends,
  countKinds,
  defaultPair,
  departmentChanges,
  firstToLast,
  letter,
  optionText,
  runDate,
  scoreOf,
  sortRuns,
  type CompanyGroup,
  type CompareData,
  type CompareRun,
  type Series,
} from './compareMath';

/**
 * Compare over time — the OML page's second tab, ported from EOS Lite's
 * OmlCompare: the overall (or one department's) score across every result as
 * one line per company, a department × result table grouped by company with
 * each company's change, and two results side by side down to the answers
 * that moved. Export PDF prints this tab; choose "Save as PDF".
 *
 * Within one question set at a time. OML v1, v2 and v3 ask different
 * questions, so a v2 result and a v3 result are not the same instrument and a
 * line through both would be a line through nothing.
 */

const num = (v: number | null | undefined) => (v === null || v === undefined ? '—' : v.toFixed(2));
const shortDate = (iso: string) => formatDate(iso);

function Delta({ value, digits = 2 }: { value: number | null; digits?: number }) {
  if (value === null) return <span className="muted">—</span>;
  const v = Math.round(value * 100) / 100;
  if (v === 0) return <span className="oml-delta flat">no change</span>;
  return (
    <span className={`oml-delta ${v > 0 ? 'up' : 'down'}`}>
      {v > 0 ? '▲ +' : '▼ −'}
      {Math.abs(v).toFixed(digits)}
    </span>
  );
}

function LevelDot({ score }: { score: number | null }) {
  return <span className={`oml-dot oml-meter-fill ${maturityToneClass(score)}`} aria-hidden="true" />;
}

const LEVELS = [1, 2, 3, 4, 5];

/* --- Trend ---------------------------------------------------------------- */

/**
 * One score (1-5) over time, one line per company. The x-axis has a slot per
 * distinct result date, oldest to newest, evenly spaced. Hovering a date shows
 * every company's result on it, and the change from that company's previous.
 */
function TrendChart({ runs, series }: { runs: CompareRun[]; series: Series }) {
  const { dates, lines } = useMemo(() => companyTrends(runs, series), [runs, series]);
  const [hover, setHover] = useState<number | null>(null);
  const W = 680;
  const H = 240;
  const L = 112;
  const R = 52;
  const T = 14;
  const B = 36;
  const x = (i: number) => (dates.length === 1 ? (L + W - R) / 2 : L + (i * (W - L - R)) / (dates.length - 1));
  const y = (v: number) => T + ((5 - v) / 4) * (H - T - B);
  const every = Math.ceil(dates.length / 6);
  const many = lines.length > 1;
  const tips =
    hover === null
      ? []
      : lines.flatMap((ln) => ln.points.filter((p) => p.slot === hover).map((p) => ({ ...p, company: ln.company, colourSlot: ln.colourSlot })));
  const tipY = Math.min(...tips.map((p) => (p.value !== null ? y(p.value) : T)), H - B);

  return (
    <div className="oml-trend">
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Maturity score by assessment date, one line per company">
        {LEVELS.map((level) => (
          <g key={level}>
            <line x1={L} x2={W - R} y1={y(level)} y2={y(level)} className="oml-trend-grid" />
            <text x={L - 10} y={y(level)} dy="0.35em" textAnchor="end" className="oml-trend-axis">
              {maturityLabel(level).replace('Level ', '')}
            </text>
          </g>
        ))}
        {dates.map(
          (dt, i) =>
            (i % every === 0 || i === dates.length - 1) && (
              <text key={dt} x={x(i)} y={H - 12} textAnchor="middle" className="oml-trend-axis">
                {/* The day too: several results in one month would otherwise all
                    read "Oct 2026". The year only when the results span more than one. */}
                {new Date(`${dt}T00:00:00`).toLocaleDateString([], {
                  month: 'short',
                  day: 'numeric',
                  ...(dates[0]!.slice(0, 4) !== dates[dates.length - 1]!.slice(0, 4) ? { year: '2-digit' as const } : {}),
                })}
              </text>
            ),
        )}
        {hover !== null && <line x1={x(hover)} x2={x(hover)} y1={T} y2={H - B} className="oml-trend-cross" />}
        {lines.map((ln) => {
          const drawn = ln.points.filter((p): p is typeof p & { value: number } => p.value !== null);
          const last = drawn[drawn.length - 1];
          return (
            <g key={ln.key} className={`oml-co-${ln.colourSlot + 1}`}>
              <path d={drawn.map((p, k) => `${k ? 'L' : 'M'}${x(p.slot)},${y(p.value)}`).join(' ')} className="oml-trend-line" />
              {drawn.map((p) => (
                <circle key={p.id} cx={x(p.slot)} cy={y(p.value)} r={hover === p.slot ? 6 : 4.5} className="oml-trend-dot" />
              ))}
              {last && (
                <text x={x(last.slot) + 10} y={y(last.value)} dy="0.35em" className="oml-trend-value">
                  {num(last.value)}
                </text>
              )}
            </g>
          );
        })}
        {dates.map((dt, i) => (
          <rect
            key={dt}
            x={x(i) - 22}
            y={T}
            width={44}
            height={H - T - B}
            fill="transparent"
            onMouseEnter={() => setHover(i)}
            onMouseLeave={() => setHover(null)}
          />
        ))}
      </svg>
      {tips.length > 0 && hover !== null && (
        <div className="oml-trend-tip" style={{ left: `${(x(hover) / W) * 100}%`, top: `${(tipY / H) * 100}%` }}>
          {tips.map((tip) => (
            <div key={tip.id} className="oml-trend-tip-row">
              <strong>
                {many && <span className={`oml-swatch oml-co-${tip.colourSlot + 1}`} />}
                {tip.label}
              </strong>
              <div className="xs muted">{shortDate(tip.date)}</div>
              {tip.value !== null ? (
                <div>
                  {num(tip.value)} avg · {maturityPercent(tip.value)}% · {maturityLabel(tip.value)}
                </div>
              ) : (
                <div className="muted">Not scored in this result</div>
              )}
              {tip.prev !== null && tip.value !== null && (
                <div>
                  <Delta value={tip.value - tip.prev} /> vs previous
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Which line is which company, and how far each has moved, first to latest. */
function TrendLegend({ runs, series }: { runs: CompareRun[]; series: Series }) {
  const { lines } = companyTrends(runs, series);
  return (
    <div className="oml-trend-legend">
      {lines.map((ln) => {
        const scored = ln.points.filter((p) => p.value !== null);
        const a = scored[0];
        const b = scored[scored.length - 1];
        return (
          <span key={ln.key} className="oml-trend-key">
            <span className={`oml-swatch oml-co-${ln.colourSlot + 1}`} />
            <strong>{ln.company}</strong>
            {a && <span className="muted">{scored.length > 1 ? `${num(a.value)} → ${num(b!.value)}` : num(a.value)}</span>}
            {a && b && scored.length > 1 && <Delta value={b.value! - a.value!} />}
          </span>
        );
      })}
    </div>
  );
}

/* --- Two results ----------------------------------------------------------- */

function RunOptions({ companies }: { companies: CompanyGroup[] }) {
  if (companies.length === 1) {
    return (
      <>
        {companies[0]!.runs.map((r) => (
          <option key={r.AssessmentId} value={r.AssessmentId}>
            {r.Title} · {shortDate(runDate(r))}
          </option>
        ))}
      </>
    );
  }
  return (
    <>
      {companies.map((g) => (
        <optgroup key={g.key} label={g.company}>
          {g.runs.map((r) => (
            <option key={r.AssessmentId} value={r.AssessmentId}>
              {r.Title} · {shortDate(runDate(r))}
            </option>
          ))}
        </optgroup>
      ))}
    </>
  );
}

const FILTERS: [key: 'changed' | 'up' | 'down' | 'all', label: string][] = [
  ['changed', 'Changed'],
  ['up', 'Improved'],
  ['down', 'Declined'],
  ['all', 'All answers'],
];

/* --- The tab --------------------------------------------------------------- */

export function OmlCompare() {
  const data = useResource<CompareData>('/assessments/compare');
  const [templateId, setTemplateId] = useState<number | null>(null);
  const [series, setSeries] = useState<Series>('overall');
  const [fromId, setFromId] = useState<number | null>(null);
  const [toId, setToId] = useState<number | null>(null);
  const [filter, setFilter] = useState<(typeof FILTERS)[number][0]>('changed');

  /* The question set with the most results, then the newest version, unless
     one has been picked. */
  const templates = data.data?.templates ?? [];
  const countFor = (id: number) => (data.data?.assessments ?? []).filter((a) => a.TemplateId === id).length;
  const template =
    templates.find((t) => t.TemplateId === templateId) ??
    [...templates].sort((a, b) => countFor(b.TemplateId) - countFor(a.TemplateId) || b.Version - a.Version)[0];

  const runs = useMemo(
    () => sortRuns((data.data?.assessments ?? []).filter((a) => a.TemplateId === template?.TemplateId)),
    [data.data, template?.TemplateId],
  );
  const companies = useMemo(() => companyGroups(runs), [runs]);
  const grouped = companies.length > 1;
  const charted = companies.filter((g) => g.slot !== null).length;

  if (data.loading) return <Loading label="Reading assessments" />;
  if (data.error) return <ErrorBanner message={data.error} />;
  if (!template) {
    return (
      <Empty title="Nothing to compare yet">
        <p>Score at least two assessments to compare them over time.</p>
      </Empty>
    );
  }

  const pair = defaultPair(runs);
  const from = runs.find((r) => r.AssessmentId === fromId) ?? pair.from;
  const to = runs.find((r) => r.AssessmentId === toId) ?? pair.to;
  const dims = template.dimensions;
  const seriesName = series === 'overall' ? 'Company overall' : (dims.find((d) => d.DimensionId === series)?.Name ?? '');

  const templatePicker =
    templates.length > 1 ? (
      <select
        className="select no-print"
        style={{ width: 'auto' }}
        value={template.TemplateId}
        onChange={(e) => {
          setTemplateId(Number(e.target.value));
          setSeries('overall');
          setFromId(null);
          setToId(null);
        }}
      >
        {templates.map((t) => (
          <option key={t.TemplateId} value={t.TemplateId}>
            {t.Name} v{t.Version} ({countFor(t.TemplateId)})
          </option>
        ))}
      </select>
    ) : null;

  if (runs.length < 2 || !from || !to) {
    return (
      <div className="stack">
        {templatePicker}
        <Empty title="Nothing to compare yet">
          <p>
            Score at least two assessments on this question set to compare them over time.
            {runs.length === 1 && ` One so far — ${runs[0]!.Title}.`}
          </p>
        </Empty>
      </div>
    );
  }

  const unnamed = runs.filter((r) => !r.Company?.trim()).length;
  const first = runs[0]!;
  const latest = runs[runs.length - 1]!;
  const changes = answerChanges(dims, from, to);
  const counts = countKinds(changes);
  const shown = changes.filter((c) =>
    filter === 'all' ? true : filter === 'changed' ? c.kind === 'up' || c.kind === 'down' : c.kind === filter,
  );
  const filterCount: Record<(typeof FILTERS)[number][0], number> = {
    changed: counts.up + counts.down,
    up: counts.up,
    down: counts.down,
    all: changes.length,
  };
  const byDimension = dims
    .map((d) => ({ dimension: d, items: shown.filter((c) => c.dimension.DimensionId === d.DimensionId) }))
    .filter((g) => g.items.length > 0);

  return (
    <div className="stack oml-compare">
      <div className="row-between wrap no-print">
        <span className="xs muted">
          Export PDF prints this tab as it stands: the trend, the table, and the two results picked below.
        </span>
        <div className="row wrap">
          {templatePicker}
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            title="Opens the print dialog — choose “Save as PDF” as the destination"
            onClick={() => window.print()}
          >
            Export PDF
          </button>
        </div>
      </div>

      {/* Trend */}
      <section className="card">
        <div className="card-head">
          <div className="grow">
            <span className="eyebrow">Maturity over time</span>
            <h2>{seriesName}</h2>
            <p className="sub">
              {runs.length} results{grouped && ` across ${companies.length} companies`} from {shortDate(runDate(first))} to{' '}
              {shortDate(runDate(latest))}
              {!grouped && first.overall !== null && latest.overall !== null && series === 'overall' && (
                <>
                  {' '}
                  · {num(first.overall)} → {num(latest.overall)} <Delta value={latest.overall - first.overall} />
                </>
              )}
              {charted < companies.length && ` · the chart shows the first ${charted} companies; the table below has them all`}
            </p>
            {/* Said out loud, because otherwise they quietly share one
                unlabelled line and it reads as two companies merged. */}
            {unnamed > 0 && (
              <p className="sub">
                {unnamed === runs.length ? 'None of these results has' : `${unnamed} of these results ${unnamed === 1 ? 'has' : 'have'} no`}
                {unnamed === runs.length ? ' a' : ''} company, so {unnamed === 1 ? 'it shares' : 'they share'} one “No company” line.
                Set the company with Edit on the Assessments tab, or Sync from eos.cmittechs.com, to give each company its own line.
              </p>
            )}
          </div>
          <select
            className="select"
            style={{ width: 'auto' }}
            value={String(series)}
            onChange={(e) => setSeries(e.target.value === 'overall' ? 'overall' : Number(e.target.value))}
          >
            <option value="overall">Company overall</option>
            {dims.map((d) => (
              <option key={d.DimensionId} value={d.DimensionId}>
                {d.Name}
              </option>
            ))}
          </select>
        </div>
        <div className="card-body">
          {grouped && <TrendLegend runs={runs} series={series} />}
          <TrendChart runs={runs} series={series} />
        </div>
      </section>

      {/* Department × result */}
      <section className="card">
        <div className="card-head">
          <div className="grow">
            <span className="eyebrow">By department</span>
            <p className="sub">Average score (1–5) in each result{grouped ? ', grouped by company, with each company’s change first to latest' : ', with the change first to latest'}.</p>
          </div>
        </div>
        <div className="table-wrap">
          <table className="table oml-matrix">
            <thead>
              {grouped && (
                <tr>
                  <th />
                  {companies.map((g) => (
                    <th key={g.key} colSpan={g.runs.length + 1} className="oml-matrix-co">
                      {g.slot !== null && <span className={`oml-swatch oml-co-${g.slot + 1}`} />} {g.company}
                    </th>
                  ))}
                </tr>
              )}
              <tr>
                <th>Department</th>
                {companies.map((g) => (
                  <Fragment key={g.key}>
                    {g.runs.map((r) => (
                      <th key={r.AssessmentId} title={r.Title} className="oml-matrix-run">
                        <div className="oml-matrix-name">{r.Title}</div>
                        {shortDate(runDate(r))}
                      </th>
                    ))}
                    <th className="oml-matrix-change">Change</th>
                  </Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {[{ key: 'overall' as const, name: 'Company overall' }, ...dims.map((d) => ({ key: d.DimensionId, name: d.Name }))].map(
                (row) => (
                  <tr key={row.key} className={row.key === 'overall' ? 'oml-matrix-total' : undefined}>
                    <td className="strong">{row.name}</td>
                    {companies.map((g) => (
                      <Fragment key={g.key}>
                        {g.runs.map((r) => {
                          const v = scoreOf(r, row.key);
                          return (
                            <td key={r.AssessmentId} className="mono" title={v === null ? 'Not scored in this result' : maturityLabel(v)}>
                              {v === null ? (
                                <span className="muted">—</span>
                              ) : (
                                <>
                                  <LevelDot score={v} /> {num(v)}
                                </>
                              )}
                            </td>
                          );
                        })}
                        <td className="oml-matrix-change">
                          <Delta value={firstToLast(g.runs.map((r) => scoreOf(r, row.key)))} />
                        </td>
                      </Fragment>
                    ))}
                  </tr>
                ),
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Two results side by side */}
      <section className="card">
        <div className="card-head">
          <div className="grow">
            <span className="eyebrow">Compare two results</span>
          </div>
          <div className="row wrap">
            <select className="select" style={{ width: 'auto' }} value={from.AssessmentId} onChange={(e) => setFromId(Number(e.target.value))}>
              <RunOptions companies={companies} />
            </select>
            <span className="muted">→</span>
            <select className="select" style={{ width: 'auto' }} value={to.AssessmentId} onChange={(e) => setToId(Number(e.target.value))}>
              <RunOptions companies={companies} />
            </select>
          </div>
        </div>
        <div className="card-body stack">
          {from.AssessmentId === to.AssessmentId ? (
            <p className="muted">Pick two different results to see what changed.</p>
          ) : (
            <>
              <div className="oml-diff-summary">
                {[from, to].map((r, i) => (
                  <Fragment key={r.AssessmentId}>
                    {i === 1 && (
                      <div className="oml-diff-mid">
                        <Delta value={r.overall !== null && from.overall !== null ? r.overall - from.overall : null} />
                        <div className="xs muted">overall</div>
                      </div>
                    )}
                    <div>
                      <div className="eyebrow">{[r.Company, r.Title].filter(Boolean).join(' · ')}</div>
                      <div className="xs muted">{shortDate(runDate(r))}</div>
                      <div className="oml-figure">{num(r.overall)}</div>
                      <span className={`badge oml-badge ${maturityToneClass(r.overall)}`}>{maturityLabel(r.overall)}</span>
                    </div>
                  </Fragment>
                ))}
                <div className="oml-diff-counts">
                  <div>
                    <span className="oml-delta up">▲ {counts.up}</span> answers improved
                  </div>
                  <div>
                    <span className="oml-delta down">▼ {counts.down}</span> answers declined
                  </div>
                  <div>
                    <span className="oml-delta flat">{counts.same}</span> unchanged
                  </div>
                  {counts.added + counts.removed > 0 && (
                    <div className="muted">{counts.added + counts.removed} answered in only one result</div>
                  )}
                </div>
              </div>

              <div>
                <h3 className="oml-diff-title">Department changes</h3>
                <div className="oml-dept-changes">
                  {departmentChanges(dims, from, to).map((d) => (
                    <div key={d.DimensionId} className="oml-dept-change">
                      <span className="strong">{d.Name}</span>
                      <span className="mono">
                        {num(d.from)} → {num(d.to)}
                      </span>
                      <Delta value={d.delta} />
                      <span className="oml-dept-levels">
                        {d.from !== null && d.to !== null && maturityLevel(d.from) !== maturityLevel(d.to) ? (
                          <>
                            <span className={`badge oml-badge ${maturityToneClass(d.from)}`}>{maturityLabel(d.from)}</span> →{' '}
                            <span className={`badge oml-badge ${maturityToneClass(d.to)}`}>{maturityLabel(d.to)}</span>
                          </>
                        ) : (
                          <span className={`badge oml-badge ${maturityToneClass(d.to ?? d.from)}`}>{maturityLabel(d.to ?? d.from)}</span>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="oml-diff-title">Answer changes</h3>
                <div className="row wrap mb-4 no-print">
                  {FILTERS.map(([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      className={`btn btn-sm ${filter === key ? 'btn-primary' : 'btn-ghost'}`}
                      onClick={() => setFilter(key)}
                    >
                      {label} ({filterCount[key]})
                    </button>
                  ))}
                </div>
                {byDimension.length === 0 ? (
                  <p className="muted">{changes.length ? 'No answers match this filter.' : 'Neither result has any answers to compare.'}</p>
                ) : (
                  <div className="stack">
                    {byDimension.map(({ dimension, items }) => (
                      <div key={dimension.DimensionId}>
                        <div className="oml-diff-dept">{dimension.Name}</div>
                        {items.map((c) => (
                          <div key={c.criterion.CriterionId} className={`oml-diff-item ${c.kind}`}>
                            <p className="oml-diff-q">{c.criterion.Text}</p>
                            <div className="oml-diff-answers">
                              <AnswerSide score={c.before} text={optionText(c.criterion, c.before)} />
                              <span className="oml-diff-arrow">
                                {c.kind === 'up' || c.kind === 'down' ? (
                                  <Delta value={c.after! - c.before!} digits={0} />
                                ) : (
                                  <span className="muted">→</span>
                                )}
                              </span>
                              <AnswerSide score={c.after} text={optionText(c.criterion, c.after)} on />
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}

function AnswerSide({ score, text, on = false }: { score: number | null; text: string | null; on?: boolean }) {
  if (score === null) return <div className="oml-diff-side muted">Not answered</div>;
  return (
    <div className="oml-diff-side">
      <span className={`oml-letter${on ? ' on' : ''}`}>{letter(score)}</span>
      <span>{text ?? maturityLabel(score)}</span>
    </div>
  );
}

